// @ts-check
/** @typedef {import('../types/api').ProductVariant} ProductVariant */

import { Edit2, Plus, Trash2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { adminProductVariantService } from '../services/adminProductExtrasService';
import api from '../utils/apiClient';
import FileUpload from './FileUpload';
import ImagePreviewButton from './ui/ImagePreviewButton';

// First 2 characters of each word in a color name, e.g. "Light Green" -> "LIGR". Mirrors
// the backend's _color_sku_abbrev in routes/admin/product_extras.py — keep both in sync.
const colorSkuAbbrev = (color) => {
  if (!color) return '';
  return color
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.slice(0, 2))
    .join('')
    .toUpperCase();
};

const buildDefaultSku = (productSlug, size, color) => {
  const parts = [productSlug, size, colorSkuAbbrev(color)].filter(Boolean);
  if (!parts.length) return '';
  return parts.join('-').toUpperCase().replace(/\s+/g, '-');
};

const ProductVariantsManager = ({ productId, productSlug, variants, onVariantsChange, productImages = [] }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [sizeOptions, setSizeOptions] = useState([]);
  const [loadingSizes, setLoadingSizes] = useState(true);
  const [showBulkCreate, setShowBulkCreate] = useState(false);
  const [selectedBulkSizes, setSelectedBulkSizes] = useState([]);
  const [bulkMode, setBulkMode] = useState('flat'); // 'flat' | 'fill' | 'duplicate'
  const [bulkColor, setBulkColor] = useState('');
  const [bulkSourceColor, setBulkSourceColor] = useState('');
  const [bulkTargetColor, setBulkTargetColor] = useState('');
  const [bulkTargetColorHex, setBulkTargetColorHex] = useState('');
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [formData, setFormData] = useState({
    size: '',
    size_variant_id: null,
    color: '',
    color_hex: '',
    image_url: '',
    sku: '',
    stock_quantity: '0',
    price_adjustment: '0',
    is_available: true,
  });

  // Fetch size variants from DB
  useEffect(() => {
    const fetchSizes = async () => {
      try {
        const data = await api.get('/api/admin/catalog/size-variants');
        setSizeOptions(data);
      } catch (error) {
        console.error('Error fetching size variants:', error);
        setSizeOptions([]);
      } finally {
        setLoadingSizes(false);
      }
    };

    fetchSizes();
  }, []);

  const resetForm = () => {
    setFormData({
      size: '',
      size_variant_id: null,
      color: '',
      color_hex: '',
      image_url: '',
      sku: '',
      stock_quantity: '0',
      price_adjustment: '0',
      is_available: true,
    });
    setShowAddForm(false);
    setEditingId(null);
    setShowImagePicker(false);
  };

  const handleAdd = async () => {
    if (!formData.size && !formData.color) {
      alert('Please provide at least size or color');
      return;
    }

    const parsed = {
      ...formData,
      stock_quantity: parseInt(String(formData.stock_quantity)) || 0,
      price_adjustment: parseFloat(String(formData.price_adjustment)) || 0,
    };

    if (productId) {
      // Persist immediately for existing products
      try {
        const saved = await adminProductVariantService.addProductVariant(productId, parsed);
        onVariantsChange([...variants, saved]);
      } catch (err) {
        console.error('Failed to add variant:', err);
        alert('Failed to add variant');
        return;
      }
    } else {
      onVariantsChange([...variants, { id: `temp_${Date.now()}`, ...parsed }]);
    }
    resetForm();
  };

  const handleEdit = (variant) => {
    setFormData({
      size: variant.size || '',
      size_variant_id: variant.size_variant_id || null,
      color: variant.color || '',
      color_hex: variant.color_hex || '',
      image_url: variant.image_url || '',
      sku: variant.sku || '',
      stock_quantity: String(variant.stock_quantity || 0),
      price_adjustment: String(variant.price_adjustment || 0),
      is_available: variant.is_available ?? true,
    });
    setEditingId(variant.id);
    setShowAddForm(true);
    setShowImagePicker(false);
  };

  const handleUpdate = async () => {
    const parsed = {
      ...formData,
      stock_quantity: parseInt(String(formData.stock_quantity)) || 0,
      price_adjustment: parseFloat(String(formData.price_adjustment)) || 0,
    };
    const updatedVariants = variants.map((v) => (v.id === editingId ? { ...v, ...parsed } : v));
    onVariantsChange(updatedVariants);
    // Persist to backend for saved variants
    if (productId && !String(editingId).startsWith('temp_')) {
      try {
        await adminProductVariantService.updateProductVariant(productId, editingId, parsed);
      } catch (err) {
        console.error('Failed to update variant:', err);
        alert('Failed to update variant');
        // This is now the only place a variant edit is written (the modal's Save no longer
        // re-PUTs every variant), so don't leave the row showing values the server rejected.
        onVariantsChange(variants);
      }
    }
    resetForm();
  };

  const handleDelete = async (variantId) => {
    if (!window.confirm('Delete this variant?')) return;
    // Only call API for persisted variants (non-temp IDs)
    if (productId && !variantId.toString().startsWith('temp_')) {
      try {
        await adminProductVariantService.deleteProductVariant(productId, variantId);
      } catch (err) {
        console.error('Failed to delete variant:', err);
        alert('Failed to delete variant');
        return;
      }
    }
    onVariantsChange(variants.filter((v) => v.id !== variantId));
  };

  const handleToggleAvailability = async (variantId) => {
    const variant = variants.find((v) => v.id === variantId);
    if (!variant) return;
    const updatedVariants = variants.map((v) => (v.id === variantId ? { ...v, is_available: !v.is_available } : v));
    onVariantsChange(updatedVariants);
    // Persist to backend for saved variants
    if (productId && !variantId.toString().startsWith('temp_')) {
      try {
        await adminProductVariantService.updateProductVariant(productId, variantId, {
          is_available: !variant.is_available,
        });
      } catch (err) {
        console.error('Failed to toggle availability:', err);
        onVariantsChange(variants); // revert
      }
    }
  };

  const toggleBulkSize = (sizeName) => {
    setSelectedBulkSizes((prev) =>
      prev.includes(sizeName) ? prev.filter((s) => s !== sizeName) : [...prev, sizeName],
    );
  };

  // Distinct colors already in use on this product, in first-seen order.
  const distinctColors = [...new Set(variants.map((v) => v.color).filter(Boolean))];

  const sizesForColor = (color) => new Set(variants.filter((v) => (v.color || '') === color).map((v) => v.size));

  const missingSizesForColor = (color) => {
    const taken = sizesForColor(color);
    return sizeOptions.filter((s) => !taken.has(s.primary_name));
  };

  const openBulkCreate = () => {
    const firstColor = distinctColors[0] || '';
    setBulkMode(distinctColors.length > 0 ? 'fill' : 'flat');
    setBulkColor(firstColor);
    setBulkSourceColor(firstColor);
    setBulkTargetColor('');
    setBulkTargetColorHex('');
    setSelectedBulkSizes([]);
    setShowBulkCreate(true);
  };

  const closeBulkCreate = () => {
    setShowBulkCreate(false);
    setSelectedBulkSizes([]);
  };

  // Shared insert path for all three bulk-create modes. sku is left blank so the
  // backend auto-generates a color-aware SKU (product slug + size + color abbreviation).
  const bulkInsert = async (items) => {
    const withMeta = items.map((item) => ({
      ...item,
      size_variant_id: sizeOptions.find((s) => s.primary_name === item.size)?.id ?? null,
      sku: '',
      is_available: true,
    }));

    if (productId) {
      try {
        const savedVariants = [];
        for (const item of withMeta) {
          const saved = await adminProductVariantService.addProductVariant(productId, item);
          savedVariants.push(saved);
        }
        onVariantsChange([...variants, ...savedVariants]);
      } catch (err) {
        console.error('Failed to bulk create variants:', err);
        alert('Failed to create some variants');
      }
    } else {
      const newVariants = withMeta.map((item, i) => ({ id: `temp_${Date.now()}_${i}`, ...item }));
      onVariantsChange([...variants, ...newVariants]);
    }
    closeBulkCreate();
  };

  // Mode: fill the sizes a color is still missing, copying stock/price from an existing variant of that color.
  const createFillSizesVariants = async () => {
    if (selectedBulkSizes.length === 0) return;
    const template = variants.find((v) => (v.color || '') === bulkColor);
    await bulkInsert(
      selectedBulkSizes.map((sizeName) => ({
        size: sizeName,
        color: bulkColor,
        color_hex: template?.color_hex || '',
        image_url: template?.image_url || '',
        stock_quantity: parseInt(template?.stock_quantity) || 0,
        price_adjustment: parseFloat(template?.price_adjustment) || 0,
      })),
    );
  };

  // Mode: replicate the sizes present under one color into another (new or existing) color,
  // copying each size's own stock/price from its source-color variant.
  const createDuplicateColorVariants = async () => {
    if (selectedBulkSizes.length === 0 || !bulkTargetColor.trim()) return;
    await bulkInsert(
      selectedBulkSizes.map((sizeName) => {
        const source = variants.find((v) => (v.color || '') === bulkSourceColor && v.size === sizeName);
        return {
          size: sizeName,
          color: bulkTargetColor.trim(),
          color_hex: bulkTargetColorHex || source?.color_hex || '',
          // Don't copy the source color's photo — it's a different color and needs its own.
          image_url: '',
          stock_quantity: parseInt(source?.stock_quantity) || 0,
          price_adjustment: parseFloat(source?.price_adjustment) || 0,
        };
      }),
    );
  };

  // Mode: legacy flat picker for products with no colors yet — one template for every new size.
  const createFlatVariants = async () => {
    if (selectedBulkSizes.length === 0 || variants.length === 0) return;
    const template = variants[0];
    await bulkInsert(
      selectedBulkSizes.map((sizeName) => ({
        size: sizeName,
        color: template.color || '',
        color_hex: template.color_hex || '',
        image_url: template.image_url || '',
        stock_quantity: parseInt(template.stock_quantity) || 0,
        price_adjustment: parseFloat(template.price_adjustment) || 0,
      })),
    );
  };

  const getTotalStock = () => {
    return variants.reduce((sum, v) => sum + (v.stock_quantity || 0), 0);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-medium">Product Variants</h3>
          <p className="text-sm text-muted-foreground">
            {variants.length} variant(s) • Total Stock: {getTotalStock()}
          </p>
        </div>
        {!showAddForm && (
          <button
            type="button"
            onClick={() => setShowAddForm(true)}
            className="px-4 py-2 bg-primary text-primary-foreground text-sm flex items-center gap-2 hover:bg-primary/90"
          >
            <Plus size={16} />
            Add Variant
          </button>
        )}
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="border border-border rounded-lg p-4 bg-muted/50">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <h4 className="text-sm font-medium">
              {editingId
                ? `Update Variant ${formData.size ? `(${formData.size}${formData.color ? ` - ${formData.color}` : ''})` : ''}`
                : 'Add New Variant'}
            </h4>
            <button type="button" onClick={resetForm} className="text-muted-foreground/70 hover:text-foreground">
              <X size={18} />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium mb-1">Size *</label>
              {loadingSizes ? (
                <div className="w-full px-3 py-2 border border-input rounded-lg text-sm text-muted-foreground/70">
                  Loading sizes...
                </div>
              ) : sizeOptions.length > 0 ? (
                <select
                  value={formData.size}
                  onChange={(e) => {
                    const selected = sizeOptions.find((s) => s.primary_name === e.target.value);
                    setFormData({ ...formData, size: e.target.value, size_variant_id: selected?.id ?? null });
                  }}
                  className="w-full px-3 py-2 border border-input rounded-lg text-sm focus:outline-none focus:border-foreground"
                  required
                >
                  <option value="">Select Size</option>
                  {sizeOptions.map((size) => {
                    // Build measurements display from min/max numeric fields
                    const fmtRange = (min, max) => {
                      if (min == null && max == null) return null;
                      if (min === max || max == null) return `${min}`;
                      return `${min}-${max}`;
                    };
                    const measurements = [
                      fmtRange(size.chest_min, size.chest_max),
                      fmtRange(size.waist_min, size.waist_max),
                      fmtRange(size.hips_min, size.hips_max),
                    ]
                      .filter(Boolean)
                      .join(', ');
                    const label = measurements ? `${size.primary_name} (${measurements}")` : size.primary_name;

                    return (
                      <option key={size.id} value={size.primary_name}>
                        {label}
                      </option>
                    );
                  })}
                </select>
              ) : (
                <div className="text-sm text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/50 border border-amber-200 dark:border-amber-900 rounded-lg px-3 py-2">
                  No sizes configured. Contact admin to add size options.
                </div>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">Color</label>
              <input
                type="text"
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                placeholder="Red, Blue, etc."
                className="w-full px-3 py-2 border border-input rounded-lg text-sm focus:outline-none focus:border-foreground"
              />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">Color Swatch</label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={formData.color_hex || '#000000'}
                  onChange={(e) => setFormData({ ...formData, color_hex: e.target.value })}
                  className="w-10 h-9 border border-input rounded-lg cursor-pointer"
                  title="Pick the exact shade for this variant's swatch on the product page"
                />
                <input
                  type="text"
                  value={formData.color_hex}
                  onChange={(e) => setFormData({ ...formData, color_hex: e.target.value })}
                  placeholder="#000000"
                  className="flex-1 px-3 py-2 border border-input rounded-lg text-sm focus:outline-none focus:border-foreground"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">Color Photo</label>

              {formData.image_url && (
                <div className="flex items-center gap-3 mb-3">
                  <div className="relative group shrink-0">
                    <ImagePreviewButton
                      src={formData.image_url}
                      alt=""
                      className="absolute -top-1.5 -left-1.5 p-1 bg-black/60 hover:bg-black/80 text-white rounded-full transition-colors opacity-0 pointer-coarse:opacity-100 group-hover:opacity-100 z-10"
                    />
                    <img
                      src={formData.image_url}
                      alt=""
                      className="w-14 h-18 object-cover rounded-lg border border-input"
                    />
                  </div>
                  <div className="flex flex-col items-start gap-1">
                    <button
                      type="button"
                      onClick={() => setShowImagePicker((prev) => !prev)}
                      className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      {showImagePicker ? 'Cancel' : 'Change'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setFormData((prev) => ({ ...prev, image_url: '' }));
                        setShowImagePicker(false);
                      }}
                      className="text-xs text-red-600 dark:text-red-400 hover:underline"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              )}

              {(!formData.image_url || showImagePicker) && (
                <div className="space-y-3">
                  {productImages.length > 0 && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-1.5">Choose from this product's photos:</p>
                      <div className="flex flex-wrap gap-2">
                        {productImages.map((img) => (
                          <button
                            key={img.id}
                            type="button"
                            onClick={() => {
                              setFormData((prev) => ({ ...prev, image_url: img.url }));
                              setShowImagePicker(false);
                            }}
                            className={`w-12 h-16 rounded-lg border overflow-hidden transition-colors flex-shrink-0 ${
                              formData.image_url === img.url
                                ? 'border-foreground border-2'
                                : 'border-input hover:border-foreground'
                            }`}
                            title="Use this photo"
                          >
                            <img src={img.url} alt="" className="w-full h-full object-cover" />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <div>
                    {productImages.length > 0 && (
                      <p className="text-xs text-muted-foreground mb-1.5">Or upload a new photo:</p>
                    )}
                    <FileUpload
                      uploadType="product-image"
                      accept="image/*"
                      multiple={false}
                      maxFiles={1}
                      onUploadComplete={(uploaded) => {
                        const file = uploaded.find((f) => f.uploaded && f.url);
                        if (file) {
                          setFormData((prev) => ({ ...prev, image_url: file.url }));
                          setShowImagePicker(false);
                        }
                      }}
                    />
                  </div>
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                Optional — falls back to the product's own photos on the product page if not set.
              </p>
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">SKU</label>
              <input
                type="text"
                value={formData.sku}
                onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                placeholder="PROD-001-S-RED"
                className="w-full px-3 py-2 border border-input rounded-lg text-sm focus:outline-none focus:border-foreground"
              />
              {!formData.sku && buildDefaultSku(productSlug, formData.size, formData.color) && (
                <p className="text-xs text-muted-foreground mt-1">
                  Default if left blank:{' '}
                  <span className="font-mono">{buildDefaultSku(productSlug, formData.size, formData.color)}</span>
                </p>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">Stock Quantity</label>
              <input
                type="number"
                value={formData.stock_quantity}
                onChange={(e) => setFormData({ ...formData, stock_quantity: e.target.value })}
                min={0}
                className="w-full px-3 py-2 border border-input rounded-lg text-sm focus:outline-none focus:border-foreground"
              />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">Price Adjustment (₹)</label>
              <input
                type="number"
                step="0.01"
                value={formData.price_adjustment}
                onChange={(e) => setFormData({ ...formData, price_adjustment: e.target.value })}
                placeholder="+5.00 or -5.00"
                className="w-full px-3 py-2 border border-input rounded-lg text-sm focus:outline-none focus:border-foreground"
              />
            </div>

            <div className="flex items-center pt-5">
              <input
                type="checkbox"
                id="is_available"
                checked={formData.is_available}
                onChange={(e) => setFormData({ ...formData, is_available: e.target.checked })}
                className="mr-2"
              />
              <label htmlFor="is_available" className="text-xs font-medium">
                Available for sale
              </label>
            </div>
          </div>

          <div className="flex gap-2 mt-4">
            <button
              type="button"
              onClick={editingId ? handleUpdate : handleAdd}
              className="flex-1 px-4 py-2 bg-primary text-primary-foreground text-sm hover:bg-primary/90"
            >
              {editingId
                ? `Update Variant ${formData.size ? `(${formData.size}${formData.color ? ` - ${formData.color}` : ''})` : ''}`
                : 'Add Variant'}
            </button>
            <button
              type="button"
              onClick={resetForm}
              className="px-4 py-2 border rounded-lg border-input text-sm hover:bg-muted/50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Bulk Create */}
      {variants.length > 0 && !showBulkCreate && (
        <button
          type="button"
          onClick={openBulkCreate}
          className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline"
        >
          + Create bulk variants from size chart
        </button>
      )}

      {showBulkCreate && (
        <div className="border rounded-lg p-4 bg-blue-50 dark:bg-blue-950/50 space-y-3">
          {distinctColors.length > 0 && (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setBulkMode('fill');
                  setSelectedBulkSizes([]);
                }}
                className={`px-3 py-1.5 text-xs rounded-md border ${
                  bulkMode === 'fill' ? 'bg-primary text-primary-foreground border-foreground' : 'bg-card border-input'
                }`}
              >
                Fill missing sizes
              </button>
              <button
                type="button"
                onClick={() => {
                  setBulkMode('duplicate');
                  setSelectedBulkSizes([]);
                }}
                className={`px-3 py-1.5 text-xs rounded-md border ${
                  bulkMode === 'duplicate'
                    ? 'bg-primary text-primary-foreground border-foreground'
                    : 'bg-card border-input'
                }`}
              >
                Duplicate a color
              </button>
            </div>
          )}

          {bulkMode === 'fill' && distinctColors.length > 0 && (
            <>
              <div>
                <label className="block text-xs font-medium mb-1">Color</label>
                <select
                  value={bulkColor}
                  onChange={(e) => {
                    setBulkColor(e.target.value);
                    setSelectedBulkSizes([]);
                  }}
                  className="w-full px-3 py-2 border border-input rounded-lg text-sm bg-card"
                >
                  {distinctColors.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <h4 className="text-sm font-medium">Missing sizes for {bulkColor}:</h4>
              <div className="flex flex-wrap gap-2">
                {missingSizesForColor(bulkColor).map((size) => (
                  <label
                    key={size.id}
                    className="flex items-center gap-1 text-sm bg-card px-3 py-1.5 rounded-lg border cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedBulkSizes.includes(size.primary_name)}
                      onChange={() => toggleBulkSize(size.primary_name)}
                    />
                    {size.primary_name}
                  </label>
                ))}
                {missingSizesForColor(bulkColor).length === 0 && (
                  <p className="text-sm text-muted-foreground">{bulkColor} already has every size.</p>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Copies stock and price from an existing {bulkColor} variant.
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={createFillSizesVariants}
                  disabled={selectedBulkSizes.length === 0}
                  className="px-4 py-1.5 bg-primary text-primary-foreground text-sm rounded-lg disabled:bg-muted-foreground/50"
                >
                  Create {selectedBulkSizes.length} Variant{selectedBulkSizes.length === 1 ? '' : 's'}
                </button>
                <button type="button" onClick={closeBulkCreate} className="px-4 py-1.5 text-sm border rounded-lg">
                  Cancel
                </button>
              </div>
            </>
          )}

          {bulkMode === 'duplicate' && distinctColors.length > 0 && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1">Copy sizes from</label>
                  <select
                    value={bulkSourceColor}
                    onChange={(e) => {
                      setBulkSourceColor(e.target.value);
                      setSelectedBulkSizes([]);
                    }}
                    className="w-full px-3 py-2 border border-input rounded-lg text-sm bg-card"
                  >
                    {distinctColors.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Into color</label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={bulkTargetColorHex || '#000000'}
                      onChange={(e) => setBulkTargetColorHex(e.target.value)}
                      className="w-10 h-9 border border-input rounded-lg cursor-pointer"
                    />
                    <input
                      type="text"
                      value={bulkTargetColor}
                      onChange={(e) => {
                        setBulkTargetColor(e.target.value);
                        setSelectedBulkSizes([]);
                      }}
                      placeholder="e.g. Blue"
                      className="flex-1 px-3 py-2 border border-input rounded-lg text-sm focus:outline-none focus:border-foreground"
                    />
                  </div>
                </div>
              </div>
              <h4 className="text-sm font-medium">Sizes to duplicate:</h4>
              <div className="flex flex-wrap gap-2">
                {sizeOptions
                  .filter((s) => {
                    const targetTaken = bulkTargetColor.trim() ? sizesForColor(bulkTargetColor.trim()) : new Set();
                    return sizesForColor(bulkSourceColor).has(s.primary_name) && !targetTaken.has(s.primary_name);
                  })
                  .map((size) => (
                    <label
                      key={size.id}
                      className="flex items-center gap-1 text-sm bg-card px-3 py-1.5 rounded-lg border cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedBulkSizes.includes(size.primary_name)}
                        onChange={() => toggleBulkSize(size.primary_name)}
                      />
                      {size.primary_name}
                    </label>
                  ))}
                {sizesForColor(bulkSourceColor).size === 0 && (
                  <p className="text-sm text-muted-foreground">{bulkSourceColor} has no sizes to duplicate.</p>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Copies each size&apos;s stock and price from its {bulkSourceColor} variant.
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={createDuplicateColorVariants}
                  disabled={selectedBulkSizes.length === 0 || !bulkTargetColor.trim()}
                  className="px-4 py-1.5 bg-primary text-primary-foreground text-sm rounded-lg disabled:bg-muted-foreground/50"
                >
                  Create {selectedBulkSizes.length} Variant{selectedBulkSizes.length === 1 ? '' : 's'}
                </button>
                <button type="button" onClick={closeBulkCreate} className="px-4 py-1.5 text-sm border rounded-lg">
                  Cancel
                </button>
              </div>
            </>
          )}

          {bulkMode === 'flat' && (
            <>
              <h4 className="text-sm font-medium">Select sizes to create variants for:</h4>
              <div className="flex flex-wrap gap-2">
                {sizeOptions
                  .filter((s) => !variants.some((v) => v.size === s.primary_name))
                  .map((size) => (
                    <label
                      key={size.id}
                      className="flex items-center gap-1 text-sm bg-card px-3 py-1.5 rounded-lg border cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedBulkSizes.includes(size.primary_name)}
                        onChange={() => toggleBulkSize(size.primary_name)}
                      />
                      {size.primary_name}
                    </label>
                  ))}
                {sizeOptions.filter((s) => !variants.some((v) => v.size === s.primary_name)).length === 0 && (
                  <p className="text-sm text-muted-foreground">All sizes already have variants.</p>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Each new variant will copy stock count ({variants[0]?.stock_quantity || 0}) and price adjustment (
                {'\u20B9'}
                {variants[0]?.price_adjustment || 0}) from the first variant.
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={createFlatVariants}
                  disabled={selectedBulkSizes.length === 0}
                  className="px-4 py-1.5 bg-primary text-primary-foreground text-sm rounded-lg disabled:bg-muted-foreground/50"
                >
                  Create {selectedBulkSizes.length} Variant{selectedBulkSizes.length === 1 ? '' : 's'}
                </button>
                <button type="button" onClick={closeBulkCreate} className="px-4 py-1.5 text-sm border rounded-lg">
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Variants Table */}
      {variants.length > 0 ? (
        <div className="border border-border rounded-lg overflow-x-auto">
          <table className="w-full min-w-[720px]">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Variant</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">SKU</th>
                <th className="px-4 py-2 text-center text-xs font-medium text-muted-foreground">Stock</th>
                <th className="px-4 py-2 text-center text-xs font-medium text-muted-foreground">Price Adj.</th>
                <th className="px-4 py-2 text-center text-xs font-medium text-muted-foreground">Status</th>
                <th className="px-4 py-2 text-center text-xs font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {variants.map((variant) => (
                <tr key={variant.id} className="hover:bg-muted/50">
                  <td className="px-4 py-3 text-sm">
                    <div className="font-medium flex items-center gap-2">
                      {variant.image_url ? (
                        <img
                          src={variant.image_url}
                          alt=""
                          className="w-6 h-8 object-cover rounded-md border border-input shrink-0"
                          title="Has its own photo"
                        />
                      ) : (
                        variant.color_hex && (
                          <span
                            className="inline-block w-3.5 h-3.5 rounded-full border border-input shrink-0"
                            style={{ backgroundColor: variant.color_hex }}
                            title={variant.color_hex}
                          />
                        )
                      )}
                      {[variant.size, variant.color].filter(Boolean).join(' / ') || 'Default'}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{variant.sku || '-'}</td>
                  <td className="px-4 py-3 text-sm text-center">
                    <span
                      className={`inline-block px-2 py-1 rounded-md text-xs ${
                        variant.stock_quantity === 0
                          ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'
                          : variant.stock_quantity < 10
                            ? 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300'
                            : 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
                      }`}
                    >
                      {variant.stock_quantity || 0}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-center">
                    {variant.price_adjustment
                      ? `${variant.price_adjustment > 0 ? '+' : ''}\u20B9${Number(variant.price_adjustment).toLocaleString('en-IN')}`
                      : '-'}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      type="button"
                      onClick={() => handleToggleAvailability(variant.id)}
                      className={`text-xs px-2 py-1 rounded-md ${
                        variant.is_available
                          ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
                          : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {variant.is_available ? 'Available' : 'Unavailable'}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleEdit(variant)}
                        className="text-muted-foreground hover:text-foreground"
                        title="Edit"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(variant.id)}
                        className="text-muted-foreground hover:text-red-600 dark:hover:text-red-400"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="border-2 border-dashed border-input rounded-lg p-8 text-center">
          <p className="text-sm text-muted-foreground">No variants added yet</p>
          <p className="text-xs text-muted-foreground/70 mt-1">
            Variants allow you to offer different sizes, colors, or options
          </p>
        </div>
      )}
    </div>
  );
};

export default ProductVariantsManager;
